import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import ContentCard from '@v2/shared/components/ContentCard';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import Band from './Band';
import DoItYourself from './DoItYourself';
import SectionCard from './SectionCard';
import StillNeedHelp from './StillNeedHelp';
import { CARD_GAP, EYEBROW, H2 } from './styles';
import { HelpCenterIndexProps } from './types';

const INTRO = 'Answers to the questions we hear most, and the account pages that fix them fastest.';

export default function HelpIndex({ props }: { props: HelpCenterIndexProps }) {
  const { buildUrl } = useUrlGenerator();

  return (
    <>
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Help Center' }]} />

      <Band
        title="How can we help?"
        intro={INTRO}
        searchAction={props.searchAction}
        kustomer={props.kustomer}
        categoryTitles={props.categoryTitles}
        popular={props.suggested.map((link) => ({ href: buildUrl(link.href), label: link.title }))}
      />

      <ContentCard className={CARD_GAP}>
        <p className={EYEBROW}>Browse by topic</p>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-[repeat(auto-fill,minmax(300px,1fr))]">
          {props.categories.map((category) => (
            <div
              key={category.slug}
              className="flex flex-col rounded-xl border border-solid border-slate-200 bg-white p-5 pb-4"
            >
              <h3 className="mb-1.5 mt-0 text-lg font-extrabold">
                <a href={buildUrl(category.href)} className="text-slate-900 no-underline hover:text-primary">
                  {category.title}
                </a>
              </h3>
              <p className="mb-3 mt-0 text-sm text-slate-500">{category.description}</p>

              <ul className="m-0 list-none border-0 border-t border-solid border-slate-200 p-0">
                {category.articles.map((article) => (
                  <li
                    key={article.slug}
                    className="m-0 border-0 border-b border-solid border-slate-200 py-2 text-sm last:border-b-0"
                  >
                    <a href={buildUrl(article.href)} className="font-semibold text-primary no-underline hover:underline">
                      {article.title}
                    </a>
                  </li>
                ))}
              </ul>

              {/*
               * Only when the card is holding some back. A category whose every article
               * is already listed above would otherwise offer "All 3 articles" under the
               * three of them, which reads as a second, fuller list that is not there.
               */}
              {category.count > category.articles.length ? (
                <a
                  href={buildUrl(category.href)}
                  className="mt-auto block pt-2.5 text-[13px] font-semibold text-slate-500 no-underline hover:text-primary"
                >
                  All {category.count} articles &rarr;
                </a>
              ) : null}
            </div>
          ))}
        </div>
      </ContentCard>

      <SectionCard>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <h2 className={H2}>Most read</h2>
            <ol className="m-0 list-none p-0 [counter-reset:n]">
              {props.mostRead.map((article) => (
                <li
                  key={article.slug}
                  className="relative m-0 border-0 border-t border-solid border-slate-200 py-2.5 pl-[34px] text-[15px] [counter-increment:n] first:border-t-0 before:absolute before:left-0 before:top-2.5 before:h-6 before:w-6 before:rounded-full before:bg-primary-50 before:text-center before:text-xs before:font-extrabold before:leading-6 before:text-primary-700 before:content-[counter(n)]"
                >
                  <a href={buildUrl(article.href)} className="font-semibold text-primary no-underline hover:underline">
                    {article.title}
                  </a>
                </li>
              ))}
            </ol>
          </div>

          <DoItYourself />
        </div>
      </SectionCard>

      <StillNeedHelp />
    </>
  );
}
